import { useContext, useEffect } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { keepPreviousData, queryClient } from '../queryClient';
import { SessionContext } from 'auth/SessionProvider';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import {
  getEventCenter,
  getNotificationEvents,
  markNotificationEventsSeen,
} from 'services/notifications';
import { EventCenterFilters } from 'types/notification';

const RUNNING_INTERVAL_MS = 5 * 1000;
const IDLE_INTERVAL_MS = 60 * 1000;

// The requests that start an action the bell follows: connect, upgrade (one cluster or many) and backup.
const STARTS_ACTION =
  /\/instances\/(?:[^/]+\/)?(?:connect|upgrade)$|\/backups\/backup$/;

// Checks every 5s while one of the admin's actions runs, every minute otherwise, and not at all while the tab is
// hidden (react-query's default); coming back to the tab checks at once.
export function useNotificationEvents() {
  const { user } = useContext(SessionContext);

  // Starting an action refreshes the bell straight away, so it shows as running without waiting for the next check.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(response => {
      const { method, url = '' } = response.config;
      if (method !== 'get' && STARTS_ACTION.test(url))
        queryClient.invalidateQueries({
          queryKey: [QueryServerKeys.NOTIFICATION.EVENTS],
        });
      return response;
    });
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  return useQuery({
    queryKey: [QueryServerKeys.NOTIFICATION.EVENTS, user?.uuid],
    queryFn: () => getNotificationEvents(),
    enabled: !!user?.uuid,
    refetchInterval: query =>
      query.state.data?.data.running.length
        ? RUNNING_INTERVAL_MS
        : IDLE_INTERVAL_MS,
  });
}

export function useMarkNotificationEventsSeen() {
  const { user } = useContext(SessionContext);

  return useMutation({
    mutationFn: (until: string) => markNotificationEventsSeen(until),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QueryServerKeys.NOTIFICATION.EVENTS, user?.uuid],
      }),
  });
}

// Asked only while the page is open; re-checks every 10s while a listed action is still running.
export function useEventCenter(filters: EventCenterFilters) {
  return useQuery({
    queryKey: [QueryServerKeys.NOTIFICATION.EVENT_CENTER, filters],
    queryFn: () => getEventCenter(filters),
    placeholderData: keepPreviousData,
    refetchInterval: query =>
      query.state.data?.data.events.some(
        event => event.status === 'in_progress',
      )
        ? 10 * 1000
        : false,
  });
}
